import { Alert, App, Button, Card, Empty, Table, Typography } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { queryKeys } from '@/shared/api/queryKeys'
import { translateError } from '@/shared/api/errors'
import { formatDateTime, formatDuration } from '@/shared/lib/format'
import { OperationResultAlert } from '@/shared/ui/OperationResultAlert'
import { RowActions } from '@/shared/ui/table/RowActions'
import { useOperationRunner } from '@/shared/ui/useOperationRunner'
import { closeStaleSession, fetchReconciliation, fetchStaleSessions, type StaleSession } from './sessionApi'

/**
 * Osilib qolgan sessiyalar.
 *
 * Bular routerda allaqachon tugagan, lekin `Accounting-Stop` yetib
 * kelmagani uchun bazada ochiq qolgan yozuvlar. Ularga Disconnect yuborib
 * bo'lmaydi (NAK qaytaradi) — yagona yo'l bazada yopish.
 *
 * ⚠️ Bu BILLING YOZUVIGA tegiladigan yagona amal, shuning uchun tasdiqlash
 * so'raladi va natija audit jurnaliga operator ismi bilan tushadi.
 */
export function StaleSessionsPanel() {
  const { t } = useTranslation()
  // ⚠️ BUG-048 — `modal` KONTEKSTDAN: statik `Modal.confirm` React
  // daraxtidan tashqarida yangi ildiz ochadi va `ConfigProvider`
  // mavzusiga yetmaydi — qorong'i panel ustida OQ dialog chiziladi.
  const { modal } = App.useApp()
  const runner = useOperationRunner([
    queryKeys.sessions.stale,
    queryKeys.sessions.active,
    queryKeys.sessions.reconciliation,
  ])

  const stale = useQuery({
    queryKey: queryKeys.sessions.stale,
    queryFn: fetchStaleSessions,
  })

  const reconciliation = useQuery({
    queryKey: queryKeys.sessions.reconciliation,
    queryFn: fetchReconciliation,
  })

  const columns: ColumnsType<StaleSession> = [
    {
      // Sahifalanmagan ro'yxat — raqam to'g'ridan-to'g'ri `index` dan
      key: '__rowNo',
      title: t('dataTable.rowNo'),
      width: 56,
      align: 'center',
      render: (_v, _r, index) => <Typography.Text type="secondary">{index + 1}</Typography.Text>,
    },
    {
      key: 'username',
      title: t('session.subscriber'),
      width: 160,
      render: (_, row) => <Typography.Text strong>{row.session.username}</Typography.Text>,
    },
    {
      key: 'framedIp',
      title: t('session.ip'),
      width: 140,
      render: (_, row) => row.session.framedIp ?? '—',
    },
    {
      key: 'lastUpdate',
      title: t('stale.lastUpdate'),
      width: 190,
      render: (_, row) =>
        row.session.lastUpdate ? formatDateTime(row.session.lastUpdate) : t('stale.unknownTime'),
    },
    {
      key: 'silentFor',
      title: t('stale.label'),
      width: 150,
      render: (_, row) =>
        row.silentForSeconds === null ? '—' : formatDuration(row.silentForSeconds),
    },
    {
      /*
        ⚠️ Bu ustun bo'lmasa operator uchun sessiya BIR KUNI o'zi
        yo'qoladi va sababi ko'rinmaydi. Tizim nima qilmoqchi ekanini
        oldindan aytish — avtomatik amalning majburiy sherigi.
      */
      key: 'closeIn',
      title: t('stale.autoCloseIn'),
      width: 170,
      render: (_, row) => {
        if (row.closeThresholdSeconds === null || row.silentForSeconds === null) return '—'
        const left = row.closeThresholdSeconds - row.silentForSeconds
        return left <= 0 ? (
          <Typography.Text type="danger">{t('stale.autoCloseDue')}</Typography.Text>
        ) : (
          <Typography.Text type="warning">{formatDuration(left)}</Typography.Text>
        )
      },
    },
    {
      key: 'actions',
      title: '',
      width: 56,
      align: 'center',
      fixed: 'right',
      render: (_, row) => (
        <RowActions
          items={[
            {
              key: 'closeStale',
              label: t('actions.closeStale'),
              permission: 'subscriber.session.disconnect',
              danger: true,
              onClick: () => {
                modal.confirm({
                  title: t('actions.closeStaleTitle'),
                  content: t('actions.closeStaleConfirm'),
                  okButtonProps: { danger: true },
                  okText: t('actions.closeStale'),
                  cancelText: t('common.cancel'),
                  onOk: () => runner.run(() => closeStaleSession(row.session.radacctId)),
                })
              },
            },
          ]}
        />
      ),
    },
  ]

  const status = reconciliation.data

  return (
    <Card title={t('reconcile.title')}>
      {/*
        "Hali yurmagan" va "nomuvofiqlik yo'q" — BOSHQA-BOSHQA holatlar.
        Ikkalasini bo'sh ro'yxat bilan ko'rsatish operatorni tekshiruv
        o'tkazilgan deb ishontirardi, aslida esa job umuman ishlamagan.
      */}
      {status && !status.hasRun ? (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message={t('reconcile.never')}
          description={t('reconcile.neverHint')}
        />
      ) : null}

      {status?.hasRun ? (
        <Typography.Paragraph type="secondary">
          {t('reconcile.checkedAt')}: {formatDateTime(status.checkedAt)} ·{' '}
          {t('reconcile.ofActive', { stale: status.staleSessions, active: status.activeSessions })}
        </Typography.Paragraph>
      ) : null}

      {runner.result ? (
        <div style={{ marginBottom: 16 }}>
          <OperationResultAlert result={runner.result} closable onClose={runner.clear} />
        </div>
      ) : null}

      {/*
        ⚠️ XATO HOLATI — usiz panel "barcha sessiyalar normal" deb
        YOLG'ON gapirardi.

        Bu karta AYNAN nomuvofiqlikni ko'rsatish uchun bor. So'rov
        yiqilganda uni "hammasi joyida" deb talqin qilish — kartaning
        maqsadini teskarisiga aylantiradi: osilib qolgan sessiya
        abonentga soatlab pul yozib turadi, panel esa xotirjam
        ko'rinadi. Yuqoridagi «hali yurmagan» ajratmasi ham xuddi shu
        sababdan qo'yilgan.
      */}
      {stale.isError ? (
        <Alert
          type="error"
          showIcon
          message={t('dataTable.loadFailed')}
          description={translateError(stale.error, t)}
          action={
            <Button size="small" onClick={() => void stale.refetch()}>
              {t('dataTable.retry')}
            </Button>
          }
        />
      ) : (
        <Table<StaleSession>
          columns={columns}
          dataSource={stale.data ?? []}
          rowKey={(row) => row.session.radacctId}
          loading={stale.isPending}
          size="small"
          pagination={false}
          scroll={{ x: 800 }}
          locale={{
            emptyText: (
              <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('reconcile.allHealthy')} />
            ),
          }}
        />
      )}
    </Card>
  )
}
